import React, { useState } from 'react';
import { useApp } from '../App';
import Modal from './ui/Modal';
import { stripAssigneeTag, setEventAssignee } from '../api/assignments';
import { fsSet, fsLogChange } from '../lib/firebase';
import { renderCoverageMessage, smsHref } from '../lib/sms';
import { DEFAULT_COVERAGE_MESSAGES } from '../config/appConfig';
import { scheduleBatchedPublish } from '../api/schedulePublish';

// ─── REQUEST COVERAGE ─────────────────────────────────────────────────────────
// Two modes:
//   'late'   (Today)    — confirm → unassign the event right away (so the app's
//                         data reflects reality) → pick a person → prefilled sms.
//   'future' (Coming Up)— skip straight to the picker, NO unassign (you're just
//                         asking ahead) → prefilled sms.
// The sms: link opens in the user's Messages app, ready to review and send.
// V1 only — no tokenized accept-link, no tracking of whether it was sent.

export default function CoverageRequestSheet({ event, myId, mode = 'late', onClose, onDone }) {
  const { familyData, currentUser } = useApp() || {};
  const isFuture = mode === 'future';
  const [step, setStep] = useState(isFuture ? 'picker' : 'confirm'); // 'confirm' | 'picker'
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const isPickup = event._pickupChildId != null;
  const title    = stripAssigneeTag(event.summary || '');
  const allDay   = !event.start?.dateTime;
  const timeStr  = allDay ? 'All day' : new Date(event.start.dateTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

  const template = familyData?.config?.coverageMessages?.[isFuture ? 'future' : 'todayLate']
                   || DEFAULT_COVERAGE_MESSAGES[isFuture ? 'future' : 'todayLate'];

  const candidates = [
    ...(familyData?.carePartners || [])
      .filter(cp => cp.identifier !== myId && cp.phone?.trim())
      .map(cp => ({ id: cp.id, name: cp.name, phone: cp.phone, kind: 'Co-parent' })),
    ...(familyData?.sitters || [])
      .filter(s => s.sitterType === 'family' && s.active !== false && s.identifier !== myId && s.phone?.trim())
      .map(s => ({ id: s.id, name: s.firstName || s.name, phone: s.phone, kind: 'Family' })),
  ];

  async function handleUnassign() {
    if (!familyData?.id) return;
    setBusy(true);
    setErrorMsg(null);
    try {
      if (isPickup) {
        const entityId = `${event._pickupChildId}_${event._pickupDate}`;
        await fsSet(`families/${familyData.id}/pickupOverrides/${entityId}`, {
          childId: event._pickupChildId,
          date:    event._pickupDate,
          adultId: 'unassigned',
          actorId:   currentUser?.email,
          actorName: currentUser?.name,
        });
        await fsLogChange(familyData.id, {
          actorId: currentUser?.email, actorName: currentUser?.name,
          module: 'home', action: 'override_pickup',
          entityType: 'pickup', entityId,
          fromValue: myId, toValue: 'unassigned — running late',
        });
      } else {
        await setEventAssignee(event, null, {
          actorId: currentUser?.email,
          actorName: currentUser?.name,
          familyId: familyData.id,
          reason: 'running late',
        });
      }
      scheduleBatchedPublish(familyData);
      setStep('picker');
    } catch (err) {
      console.error('CoverageRequestSheet unassign failed:', err);
      setErrorMsg("Couldn't clear the assignment — try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={isFuture ? 'Ask someone to cover' : 'Running late'} onClose={onClose}>
      {step === 'confirm' && (
        <div className="coverage-sheet">
          <p className="coverage-sheet__recap">
            <strong>{title}</strong>
            <span className="coverage-sheet__recap-time">{timeStr}</span>
          </p>
          <p className="coverage-sheet__hint">
            This clears the assignment so it shows as needing coverage, then lets you text someone to cover it.
          </p>
          {errorMsg && <p className="coverage-sheet__error">{errorMsg}</p>}
          <button className="btn btn--primary" onClick={handleUnassign} disabled={busy}>
            {busy ? 'Clearing…' : 'Running late? Request coverage'}
          </button>
        </div>
      )}

      {step === 'picker' && (
        <div className="coverage-sheet">
          <p className="coverage-sheet__hint">
            {isFuture ? 'Who do you want to ask to cover this?' : 'Unassigned ✓ — who do you want to ask to cover?'}
          </p>
          {candidates.length === 0 ? (
            <p className="coverage-sheet__empty">
              No one with a phone number on file. Add one in Settings → Care Partners or Settings → Caregivers.
            </p>
          ) : (
            <div className="coverage-sheet__candidates">
              {candidates.map(c => {
                const message = renderCoverageMessage(template, {
                  eventTitle: title,
                  description: event.description,
                  start: event.start,
                });
                return (
                  <a
                    key={c.id}
                    href={smsHref(c.phone, message)}
                    className="coverage-sheet__candidate"
                    onClick={() => onDone?.()}
                  >
                    <span className="coverage-sheet__candidate-name">{c.name}</span>
                    <span className="coverage-sheet__candidate-kind">{c.kind}</span>
                  </a>
                );
              })}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
